import { getApiBaseUrl } from '../../auth/api';
import { authFetch } from '../../auth/authFetch';
import type {
  ApiResponse,
  CreateGeneralParameterRequest,
  GeneralParametersOverviewQuery,
  GeneralParametersOverviewResponse,
  ListDetailResponse,
  MapDetailResponse,
  MirrorDetailResponse,
  RegisteredTargetDto,
  UpdateParameterMapEntryRequest,
  UpdateParameterRegionsRequest,
  UpsertParameterValueRequest,
  ValidationErrorResult,
} from './generalParametersApiTypes';

interface GeneralParametersRequestError {
  status: number;
  message: string;
  validation?: ValidationErrorResult;
}

const base = () => `${getApiBaseUrl()}/api/generalparameters`;

const jsonHeaders = { 'Content-Type': 'application/json' };

const queryString = (query: GeneralParametersOverviewQuery) => {
  const params = new URLSearchParams();
  Object.entries(query).forEach(([key, value]) => {
    if (value) params.set(key, value);
  });
  const text = params.toString();
  return text ? `?${text}` : '';
};

const parseOrThrow = async <T>(response: Response): Promise<ApiResponse<T>> => {
  const body = await response.json() as ApiResponse<T | ValidationErrorResult>;
  if (!response.ok) {
    const error: GeneralParametersRequestError = {
      status: response.status,
      message: body.message ?? `Request failed: ${response.status}`,
      validation: body.result && typeof body.result === 'object' && 'errors' in body.result ? body.result as ValidationErrorResult : undefined,
    };
    throw error;
  }
  return body as ApiResponse<T>;
};

export const fetchGeneralParameterOverview = async (query: GeneralParametersOverviewQuery = {}) => {
  const res = await authFetch(`${base()}${queryString(query)}`);
  return parseOrThrow<GeneralParametersOverviewResponse>(res);
};

export const fetchRegisteredTargets = async () => {
  const res = await authFetch(`${base()}/registered-targets`);
  return parseOrThrow<RegisteredTargetDto[]>(res);
};

export const fetchGeneralParameterList = async (parameterCode: string) => {
  const res = await authFetch(`${base()}/${parameterCode}/list`);
  return parseOrThrow<ListDetailResponse>(res);
};

export const fetchGeneralParameterMap = async (parameterCode: string) => {
  const res = await authFetch(`${base()}/${parameterCode}/map`);
  return parseOrThrow<MapDetailResponse>(res);
};

export const fetchGeneralParameterMirror = async (parameterCode: string) => {
  const res = await authFetch(`${base()}/${parameterCode}/mirror`);
  return parseOrThrow<MirrorDetailResponse>(res);
};

export const createGeneralParameter = async (request: CreateGeneralParameterRequest) => {
  const res = await authFetch(base(), {
    method: 'POST',
    headers: jsonHeaders,
    body: JSON.stringify(request),
  });
  return parseOrThrow<unknown>(res);
};

export const createParameterValue = async (parameterCode: string, request: UpsertParameterValueRequest) => {
  const res = await authFetch(`${base()}/${parameterCode}/values`, {
    method: 'POST',
    headers: jsonHeaders,
    body: JSON.stringify(request),
  });
  return parseOrThrow<unknown>(res);
};

export const updateParameterValue = async (parameterCode: string, valueCode: string, request: UpsertParameterValueRequest) => {
  const res = await authFetch(`${base()}/${parameterCode}/values/${valueCode}`, {
    method: 'PUT',
    headers: jsonHeaders,
    body: JSON.stringify(request),
  });
  return parseOrThrow<unknown>(res);
};

export const updateParameterMapEntry = async (parameterCode: string, mapEntryId: string, request: UpdateParameterMapEntryRequest) => {
  const res = await authFetch(`${base()}/${parameterCode}/maps/${mapEntryId}`, {
    method: 'PUT',
    headers: jsonHeaders,
    body: JSON.stringify(request),
  });
  return parseOrThrow<unknown>(res);
};

export const updateParameterRegions = async (parameterCode: string, request: UpdateParameterRegionsRequest) => {
  const res = await authFetch(`${base()}/${parameterCode}/regions`, {
    method: 'PUT',
    headers: jsonHeaders,
    body: JSON.stringify(request),
  });
  return parseOrThrow<unknown>(res);
};
